import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, VolumeX, Volume2, Signal, AlertTriangle } from "lucide-react";

/**
 * Sparkleworld GameFrame
 * - Renders a static game bundle in an iframe: /games/<slug>/index.html
 * - Implements the host side of the postMessage protocol defined in the brief.
 * - Keeps it strict: validates message shape + slug.
 */

const HOST_SOURCE = "sparkleworld-host";
const GAME_SOURCE = "sparkleworld-game";

export default function GameFrame({
  slug,
  title,
  difficulty = 2,
  defaultMuted = true,
}) {
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [isMuted, setIsMuted] = useState(!!defaultMuted);
  const [lastScore, setLastScore] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = useMemo(() => {
    // lightweight session id for host_init
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }, []);

  const src = useMemo(() => `/games/${slug}/index.html`, [slug]);

  const postToGame = (type, payload = {}) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        source: HOST_SOURCE,
        slug,
        type,
        payload,
      },
      "*" // host validates origin in production; game validates shape+slug
    );
  };

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.source !== GAME_SOURCE) return;
      if (data.slug !== slug) return;
      if (typeof data.type !== "string") return;

      switch (data.type) {
        case "GAME_READY": {
          setIsReady(true);
          // handshake: HOST_INIT after GAME_READY
          postToGame("HOST_INIT", {
            sessionId,
            difficulty,
            muted: isMuted,
          });
          break;
        }

        case "GAME_SCORE": {
          // payload: { scoreDelta, reason, totalScore? }
          setLastScore({
            at: Date.now(),
            ...(data.payload || {}),
          });
          break;
        }

        case "GAME_COMPLETE": {
          // payload: { runId, result, stats }
          // For now: just go back to /games when complete.
          navigate("/games");
          break;
        }

        case "GAME_ERROR": {
          setError(data.payload?.message || "Game error");
          break;
        }

        case "GAME_RESIZE": {
          // optional: respect height hints (we keep a min height anyway)
          // If you want dynamic resize later, wire it here.
          break;
        }

        default:
          // ignore unknown
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [difficulty, isMuted, navigate, sessionId, slug]);

  // If the user toggles mute after the init handshake, send HOST_INIT again
  useEffect(() => {
    if (!isReady) return;
    postToGame("HOST_INIT", { sessionId, difficulty, muted: isMuted });
  }, [difficulty, isMuted, isReady, sessionId]);

  return (
    <div className="min-h-screen px-6 py-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            onClick={() => navigate("/games")}
            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-border rounded-full font-nunito font-semibold hover:shadow-hard transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="flex items-center gap-2 text-sm">
            <span
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border ${
                isReady ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <Signal className="w-4 h-4" />
              {isReady ? "Connected" : "Loading…"}
            </span>

            <button
              onClick={() => setIsMuted((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border bg-white hover:shadow-hard transition-all"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {isMuted ? "Muted" : "Sound"}
            </button>
          </div>
        </div>

        <h1 className="font-fredoka text-3xl font-bold text-foreground mb-4 text-center">
          {title || slug}
        </h1>

        {error && (
          <div className="mb-4 p-4 rounded-2xl border-2 border-red-200 bg-red-50 text-red-800 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-fredoka font-bold">Game reported an error</div>
              <div className="font-nunito text-sm">{error}</div>
            </div>
          </div>
        )}

        {lastScore && (
          <div className="mb-4 p-3 rounded-2xl border bg-white text-sm">
            <div className="font-fredoka font-bold">Latest score</div>
            <div className="font-nunito text-muted-foreground">
              +{lastScore.scoreDelta ?? 0} — {lastScore.reason || ""}
            </div>
          </div>
        )}

        <div className="rounded-3xl border-2 border-border overflow-hidden shadow-hard-lg bg-white">
          <iframe
            ref={iframeRef}
            title={title || slug}
            src={src}
            className="w-full h-[700px] md:h-[760px]"
            allow="autoplay"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>

        <div className="mt-3 text-center text-xs text-muted-foreground">
          Source: <code>{src}</code>
        </div>
      </div>
    </div>
  );
}
